import { Table, Button, Row, Col, Form, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import { useGetBillsQuery, useDeleteBillMutation } from '../../slices/billsApiSlice';
import { useGetOpeningStocksQuery } from '../../slices/openingStocksApiSlice'; // For qty and itemCost
import { useGetSuppliesQuery } from '../../slices/suppliesApiSlice'; // For Pqty and Pcost
import { toast } from 'react-toastify';
import { useState } from 'react';
import * as XLSX from "xlsx";

const BillListScreen = () => {
  const [filter, setFilter] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { data: billsData, isLoading, error, refetch } = useGetBillsQuery(pageNumber);
  const { data: openingStockData } = useGetOpeningStocksQuery();
  const { data: suppliesData } = useGetSuppliesQuery();

  const [deleteBill, { isLoading: loadingDelete }] = useDeleteBillMutation();

  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this bill?')) {
      try {
        await deleteBill(id).unwrap();
        toast.success('Bill deleted successfully!');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || 'Failed to delete bill.');
      }
    }
  };

  const filterBills = () => {
    if (!billsData?.bills) return [];
    return billsData.bills.filter((bill) => {
      const billDate = new Date(bill.date);
      const isWithinDateRange =
        (!startDate || billDate >= new Date(startDate)) &&
        (!endDate || billDate <= new Date(endDate));
      return (
        isWithinDateRange &&
        (bill.note?.toLowerCase().includes(filter.toLowerCase()) ||
          bill.customerDetails?.name?.toLowerCase().includes(filter.toLowerCase()) ||
          bill.customerDetails?.company?.toLowerCase().includes(filter.toLowerCase()) ||
          bill.customerDetails?.address?.toLowerCase().includes(filter.toLowerCase()) ||
          bill.billItems.some((item) =>
            item.name.toLowerCase().includes(filter.toLowerCase())
          ))
      );
    });
  };

  const calculatePqty = (productName) => {
    return suppliesData?.supplies
      ?.reduce((totalQty, supply) => {
        const productItems = supply.supplyItems.filter((item) => item.name === productName);
        const productQty = productItems.reduce((qty, item) => qty + item.qty, 0);
        return totalQty + productQty;
      }, 0) || 0;
  };

  const calculatePcost = (productName) => {
    return suppliesData?.supplies
      ?.reduce((totalCost, supply) => {
        const productItems = supply.supplyItems.filter((item) => item.name === productName);
        const productCost = productItems.reduce((cost, item) => cost + item.itemCost, 0);
        return totalCost + productCost;
      }, 0) || 0;
  };

  const calculateAverageCost = (productName) => {
    const openingStockItems = openingStockData?.openingStocks
      ?.flatMap((openingStock) => openingStock.openingStockItems)
      ?.filter((item) => item.name === productName);

    const qty = openingStockItems?.reduce((total, item) => total + item.qty, 0) || 0;
    const itemCost = openingStockItems?.reduce((total, item) => total + item.itemCost, 0) || 0;

    const Pqty = calculatePqty(productName);
    const Pcost = calculatePcost(productName);

    const totalQty = qty + Pqty;
    const totalCost = itemCost + Pcost;

    return totalQty > 0 ? totalCost / totalQty : 0;
  };

  const calculateTotals = () => {
    const totalBills = filterBills().reduce(
      (total, bill) =>
        total +
        bill.billItems.reduce(
          (subTotal, item) => subTotal + item.qty * item.price,
          0
        ),
      0
    );

    const totalCost = filterBills().reduce((total, bill) => {
      return total + bill.billItems.reduce((subTotal, item) => {
        const averageCost = calculateAverageCost(item.name);
        return subTotal + averageCost * item.qty;
      }, 0);
    }, 0);

    const totalProfit = totalBills - totalCost;

    return { totalBills, totalCost, totalProfit };
  };

  const { totalBills, totalCost, totalProfit } = calculateTotals();

  const paginationHandler = (page) => {
    setPageNumber(page);
  };

  const exportTableToExcel = () => {
    const tableData = filterBills().map((bill) =>
      bill.billItems.map((item) => {
        const averageCost = calculateAverageCost(item.name);
        const totalCost = averageCost * item.qty;
        const profit = item.qty * item.price - totalCost;
  
        return {
          Date: new Date(bill.date).toLocaleDateString(),
          Product: item.name,
          Qty: item.qty,
          Price: item.price.toFixed(2),
          Total: (item.qty * item.price).toFixed(2),
          GTotal: (item.qty * item.price * 1.4).toFixed(0),
          
          TotalCost: totalCost.toFixed(2),
          Profit: profit.toFixed(2),
          Note: bill.note || "N/A",
          Customer: bill.customerDetails?.name || "N/A",
          Company: bill.customerDetails?.company || "N/A",
          Telephone: bill.customerDetails?.telephone || "N/A",
          Serial: bill.customerDetails?.serialNumber || "N/A",
          Address: bill.customerDetails?.address || "N/A",
        };
      })
    ).flat();
  
    const worksheet = XLSX.utils.json_to_sheet(tableData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Bills Table Export");
    XLSX.writeFile(workbook, "BillsTable.xlsx");
  };

  return (
    <>
      {/* Header */}
      <Row className="align-items-center mb-3">
        <Col>
          <h1>Bills</h1>
          <Button variant="success" onClick={exportTableToExcel}>Export Table to Excel</Button>
        </Col>
        
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by product name or note"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>

      {/* Totals Section */}
      <Row className="mb-3">
        <Col md={4}>
          <h4>GTotal: ${(totalBills * 1.4).toFixed(2)}</h4>
        </Col>
        <Col md={4}>
          <h4>Total: ${(totalBills ).toFixed(2)}</h4>
        </Col>
        <Col md={4}>
          <h4>Total Cost: ${totalCost.toFixed(2)}</h4>
        </Col>
        <Col md={4} className="text-end">
          <h4>Total Profit: ${totalProfit.toFixed(2)}</h4>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>
      </Row>

      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'An error occurred'}
        </Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT NAME</th>
                <th>QTY</th>
                <th>TOTAL</th>
                <th>GTOTAL</th>
                <th>CUSTOMER</th>
                <th>COMPANY</th>
                <th>SERIAL</th>
                <th>SUBCOM</th>
                <th>TEL</th>
                <th>NOTE</th>
                
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filterBills().map((bill) =>
                bill.billItems.map((item) => {
                  //const averageCost = calculateAverageCost(item.name);
                  //const totalCost = averageCost * item.qty;
                  //const profit = item.qty * item.price - totalCost;

                  return (
                    <tr key={`${bill._id}-${item.product}`}>
                      <td>{new Date(bill.date).toLocaleDateString()}</td>
                      <td>{item.name}</td>
                      <td>{item.qty}</td>
                      <td>{(item.qty * item.price).toFixed(2)}</td>
                      <td>{(item.qty * item.price * 1.4).toFixed(0)}</td>
                      <td>{bill.customerDetails.name || 'N/A'}</td>
                      <td>{bill.customerDetails.company || 'N/A'}</td>
                      <td>{bill.customerDetails.serialNumber || 'N/A'}</td>
                      <td>{bill.customerDetails.address || 'N/A'}</td>
                      <td>{bill.customerDetails.telephone || 'N/A'}</td>
                      <td>{bill.note || 'N/A'}</td>
                      
                      <td  style={{display: 'none'}}>
                        {item.images && (
                          <img
                            src={item.images[0]}
                            alt="Thumbnail"
                            className="thumbnail-img me-2"
                            style={{
                              width: '40px',
                              height: '40px',
                              objectFit: 'cover',
                              borderRadius: '5px',
                            }}
                          />
                        )}
                      </td>
                      <td>
                        <Button
                          as={Link}
                          to={`/bill/${bill._id}`}
                          variant="light"
                          className="btn-sm"
                        >
                          Details
                        </Button>
                        <Button
                          as={Link}
                          to={`/admin/bill/${bill._id}/edit`}
                          variant="light"
                          className="btn-sm mx-2"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          className="btn-sm mx-2"
                          onClick={() => deleteHandler(bill._id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <Pagination>
            {[...Array(billsData.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => paginationHandler(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  );
};

export default BillListScreen;